import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ComponentsAPI } from "@/lib/api";
import { COMPONENT_TYPES, COMPONENT_TYPE_LIST } from "@/lib/componentTypes";
import logger from "@/lib/logger";

/**
 * Add/Edit dialog for a wearable component. Fields are static (vehicle,
 * brand, model, dates, position) — the Wear section renders dynamically
 * from `COMPONENT_TYPES[type].wearFields` so extending the feature to a
 * new component type is a one-file change.
 */
export default function ComponentDialog({ open, onOpenChange, vehicles = [], defaultVehicleId, initial = null, currencySymbol = "$", onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({ ...initial, wear: { ...(initial.wear || {}) } });
    } else {
      const defaultType = "tire";
      setForm({
        vehicle_id: defaultVehicleId || vehicles[0]?.id || "",
        type: defaultType,
        name: "",
        brand: "",
        model: "",
        position: "",
        status: "active",
        purchase_date: "",
        purchase_price: "",
        install_date: "",
        install_odometer_km: "",
        notes: "",
        wear: seedWearDefaults(defaultType),
      });
    }
  }, [open, initial, defaultVehicleId, vehicles]);

  if (!form) return null;
  const typeMeta = COMPONENT_TYPES[form.type] || COMPONENT_TYPES.tire;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setWear = (k, v) => setForm((f) => ({ ...f, wear: { ...f.wear, [k]: v } }));

  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Pick a vehicle");
    if (!form.type) return toast.error("Pick a component type");
    setSubmitting(true);
    const payload = {
      vehicle_id: form.vehicle_id,
      type: form.type,
      name: form.name || "",
      brand: form.brand || "",
      model: form.model || "",
      position: form.position || "",
      status: form.status || "active",
      purchase_date: form.purchase_date || null,
      purchase_price: form.purchase_price !== "" ? parseFloat(form.purchase_price) : null,
      install_date: form.install_date || null,
      install_odometer_km: form.install_odometer_km !== "" ? parseFloat(form.install_odometer_km) : null,
      notes: form.notes || "",
      wear: sanitiseWear(form.wear || {}),
    };
    try {
      const saved = isEdit ? await ComponentsAPI.update(initial.id, payload) : await ComponentsAPI.create(payload);
      toast.success(isEdit ? "Component updated" : "Component added");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      logger.error("component-save-failed", err?.message || String(err));
      toast.error("Save failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="component-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight">{isEdit ? "Edit component" : "Add component"}</DialogTitle>
          <DialogDescription className="text-xs text-[var(--text-muted)]">
            Track a physical wearable part. Wear fields adapt to the component type.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-1">
          {!isEdit && (
            <div className="space-y-1 col-span-2">
              <Label className="label-tiny">Vehicle</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setField("vehicle_id", v)}>
                <SelectTrigger className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="component-vehicle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="label-tiny">Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v, wear: seedWearDefaults(v) }))} disabled={isEdit}>
              <SelectTrigger className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="component-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                {COMPONENT_TYPE_LIST.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Status</Label>
            <Select value={form.status} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="component-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                <SelectItem value="active">Active (installed)</SelectItem>
                <SelectItem value="stored">Stored</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <TextField label="Brand" value={form.brand} onChange={(v) => setField("brand", v)} testid="component-brand" />
          <TextField label="Model" value={form.model} onChange={(v) => setField("model", v)} testid="component-model" placeholder="e.g. CrossClimate 2" />
          {typeMeta.positions?.length > 0 && (
            <div className="space-y-1 col-span-2">
              <Label className="label-tiny">Position</Label>
              <Select value={form.position || "none"} onValueChange={(v) => setField("position", v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="component-position">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                  <SelectItem value="none">—</SelectItem>
                  {typeMeta.positions.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <TextField label="Purchase Date" type="date" value={form.purchase_date} onChange={(v) => setField("purchase_date", v)} testid="component-purchase-date" />
          <TextField label={`Purchase Price (${currencySymbol})`} type="number" step="0.01" value={form.purchase_price} onChange={(v) => setField("purchase_price", v)} testid="component-purchase-price" />
          <TextField label="Install Date" type="date" value={form.install_date} onChange={(v) => setField("install_date", v)} testid="component-install-date" />
          <TextField label="Install Odometer (km)" type="number" value={form.install_odometer_km} onChange={(v) => setField("install_odometer_km", v)} testid="component-install-odo" />
        </div>

        {/* Wear section — adapts per type */}
        <div className="border border-[var(--border-hover)] bg-[var(--bg-canvas)] p-3 mt-1" style={{ borderRadius: 4 }}>
          <div className="label-tiny mb-2">Wear &amp; Usage</div>
          <div className="grid grid-cols-2 gap-3">
            {typeMeta.wearFields.map((f) => (
              <div key={f.key} className={f.type === "select" ? "space-y-1 col-span-2" : "space-y-1"}>
                <Label className="label-tiny">{f.label}</Label>
                {f.type === "select" ? (
                  <Select value={form.wear?.[f.key] || ""} onValueChange={(v) => setWear(f.key, v)}>
                    <SelectTrigger className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid={`component-wear-${f.key}`}>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)]">
                      {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type || "number"}
                    step={f.step}
                    min={f.min}
                    max={f.max}
                    value={form.wear?.[f.key] ?? ""}
                    onChange={(e) => setWear(f.key, e.target.value === "" ? "" : parseFloat(e.target.value))}
                    className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data h-9"
                    data-testid={`component-wear-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <Label className="label-tiny">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]" data-testid="component-notes" />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]" style={{ borderRadius: 2 }} data-testid="component-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="flex-1 bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]" style={{ borderRadius: 2 }} data-testid="component-save">
            {submitting ? "Saving..." : isEdit ? "Save changes" : "Add component"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TextField({ label, value, onChange, type = "text", testid, step, placeholder = "" }) {
  return (
    <div className="space-y-1">
      <Label className="label-tiny">{label}</Label>
      <Input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data h-9"
        data-testid={testid}
      />
    </div>
  );
}

function seedWearDefaults(type) {
  const cfg = COMPONENT_TYPES[type];
  if (!cfg) return {};
  const out = {};
  for (const f of cfg.wearFields) {
    if (f.defaultValue !== undefined) out[f.key] = f.defaultValue;
  }
  return out;
}

function sanitiseWear(wear) {
  const out = {};
  for (const [k, v] of Object.entries(wear)) {
    if (v === "" || v == null) continue;
    out[k] = v;
  }
  return out;
}
